import { useCallback, useEffect, useRef, useState } from "react";
import { getLikedItems, getPurchasedProducts } from "@/api/customer.api.js";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll.js";

const PAGE_SIZE = 12;

export const FAVORITES_TAB = {
    PURCHASED: "purchased",
    LIKED: "liked"
};

export function useMesFavorisList({ tab, enabled = true }) {
    const [products, setProducts] = useState([]);
    const [offset, setOffset] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");
    const offsetRef = useRef(0);

    const fetchPage = useCallback(async (nextOffset, append) => {
        if (!enabled) {
            return;
        }
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError("");
        try {
            const fetcher = tab === FAVORITES_TAB.LIKED ? getLikedItems : getPurchasedProducts;
            const response = await fetcher({ offset: nextOffset, limit: PAGE_SIZE });
            const pageItems = response.data || [];
            setProducts((prev) => (append ? [...prev, ...pageItems] : pageItems));
            setHasNext(!!response.hasNext);
            offsetRef.current = nextOffset;
            setOffset(nextOffset);
        } catch (err) {
            setError(err.message);
            if (!append) {
                setProducts([]);
                setHasNext(false);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [enabled, tab]);

    useEffect(() => {
        if (!enabled) {
            setProducts([]);
            setHasNext(false);
            setError("");
            return;
        }
        offsetRef.current = 0;
        setOffset(0);
        fetchPage(0, false);
    }, [enabled, tab, fetchPage]);

    const loadMore = useCallback(() => {
        const nextOffset = offsetRef.current + PAGE_SIZE;
        fetchPage(nextOffset, true);
    }, [fetchPage]);

    const sentinelRef = useInfiniteScroll({ hasNext, loading, loadingMore, onLoadMore: loadMore });

    return {
        products,
        loading,
        loadingMore,
        error,
        sentinelRef,
        hasProducts: products.length > 0
    };
}
